import { Language } from './i18n-config';

/**
 * Maps a count to an Arabic CLDR plural form index (0–5):
 *  _0  zero  — count = 0
 *  _1  one   — count = 1
 *  _2  two   — count = 2
 *  _3  few   — 3 ≤ (count % 100) ≤ 10
 *  _4  many  — 11 ≤ (count % 100) ≤ 99
 *  _5  other — everything else (100, 200, …)
 */
const getArabicPluralIndex = (count: number): number => {
  if (count === 0) return 0;

  if (count === 1) return 1;

  if (count === 2) return 2;

  const mod = count % 100;

  if (mod >= 3 && mod <= 10) return 3;

  if (mod >= 11 && mod <= 99) return 4;

  return 5;
};

/**
 * Returns the correct i18next translation key for a given count and language.
 *
 * Arabic has 6 grammatical plural forms that do not map onto the standard
 * singular / `_plural` pair used by other languages. For Arabic the JSON
 * translation files carry `_0` – `_5` suffixed keys (per CLDR rules) instead
 * of a `_plural` variant, so this function selects the appropriate suffix.
 *
 * For all other languages the standard i18next convention is applied:
 * the bare key for count = 1, and `<key>_plural` for any other count.
 *
 * @example
 * // Arabic, count = 5  → "savings_plans_list_title_active_3"  (few form)
 * // English, count = 5 → "savings_plans_list_title_active_plural"
 * getPluralTranslationKey('savings_plans_list_title_active', 5, language)
 */
export const getPluralTranslationKey = (
  baseKey: string,
  count: number,
  language: Language,
): string => {
  if (language === 'ar') {
    return `${baseKey}_${getArabicPluralIndex(count)}`;
  }

  return count === 1 ? baseKey : `${baseKey}_plural`;
};
